import { useMemo } from 'react';
import PropTypes from 'prop-types';

import useDetailTable from '#/hooks/table/useDetailTable';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { formatDisplayAmount, formatDisplayQty } from '#/utils/formatNumber';

function displayDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function textColumn(id, header, field, size) {
  return {
    id,
    header,
    accessorFn: (row) => displayDash(row?.[field]),
    size,
    enableSorting: false,
    meta: { cellClassName: 'td_left' },
    cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
  };
}

function numberColumn(id, header, accessorFn, size) {
  return {
    id,
    header,
    accessorFn,
    size,
    enableSorting: false,
    meta: { cellClassName: 'td_right' },
  };
}

function buildMiscPurchaseOrderLineColumns() {
  return [
    textColumn('itemName', '品名', 'itemName', 220),
    textColumn('specification', '規格', 'specification', 200),
    numberColumn('qty', '數量', (row) => formatDisplayQty(row?.qty), 100),
    numberColumn('unitPrice', '單價', (row) => formatDisplayAmount(row?.unitPrice), 120),
    numberColumn('amount', '金額', (row) => formatDisplayAmount(row?.amount), 130),
    textColumn('remark', '備註', 'remark', 200),
  ];
}

/** Detail 明細：唯讀 DetailTable（欄寬可拖曳）；金額以 BE `line.amount` 為準。 */
const MiscPurchaseOrderLinesSection = ({ lines = [] }) => {
  const columns = useMemo(() => buildMiscPurchaseOrderLineColumns(), []);
  const rows = useMemo(
    () => (lines ?? []).map((line, index) => ({
      ...line,
      id: String(line?.lineId ?? `misc-po-line-${index}`),
    })),
    [lines],
  );
  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  if (rows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無明細</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

MiscPurchaseOrderLinesSection.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object),
};

export default MiscPurchaseOrderLinesSection;
